<script lang="ts">
  // Platform diagram: train side-view with exit drop-lines and street labels. Exits arrive
  // as bare lat/lon, so the platform's own axis is recovered by PCA and each exit projected
  // onto it — what turns a scatter of coordinates into a left-to-right run of entrances.
  import type { StationExit } from '../../services/api'
  import { DIAGRAM, exitTypeColor, titleCase } from './stationExits'

  interface Props {
    exits: StationExit[]
    stationLat: number
    stationLon: number
  }

  const { exits, stationLat, stationLon }: Props = $props()

  // Train geometry (Y scaled ×0.75 for a 25% shorter diagram)
  const W = 360
  const TX1 = 36 // rear end x
  const TX2 = 295 // body-to-nose junction x
  const NOSE = 322 // nose tip x
  const TY1 = 17 // train top y
  const TY2 = 41 // train bottom y
  const N_CARS = 8
  const CAR_W = (TX2 - TX1) / N_CARS
  // Doors, not a continuous window strip: a light steel body punched with dark openings is
  // what makes a platform read as somewhere you board rather than a bar on a chart. Two per
  // car, inset from the dividers.
  const DOORS_PER_CAR = 2
  const DOOR_W = 5
  const DOOR_H = 7
  const DOOR_Y = TY1 + 5

  // Exit geometry
  const CIRCLE_Y = 65
  const DOT_R = 4
  const STEM_TOP = CIRCLE_Y + DOT_R + 2 // connector stem starts just below dot

  // Label layout
  const FIRST_LABEL_Y = 83
  const LABEL_ROW_H = 12
  const LABEL_GAP = 100
  const MAX_ROWS = 4

  const TRAIN_PATH = [
    `M ${TX1 + 7},${TY1}`,
    `L ${TX2},${TY1}`,
    `L ${NOSE},${(TY1 + TY2) / 2}`,
    `L ${TX2},${TY2}`,
    `L ${TX1 + 7},${TY2}`,
    `Q ${TX1},${TY2} ${TX1},${TY2 - 5}`,
    `L ${TX1},${TY1 + 5}`,
    `Q ${TX1},${TY1} ${TX1 + 7},${TY1}`,
    'Z',
  ].join(' ')

  const carDividers = Array.from({ length: N_CARS - 1 }, (_, i) => TX1 + (i + 1) * CAR_W)

  // Door x positions, evenly spaced inside each car.
  const doors = Array.from({ length: N_CARS * DOORS_PER_CAR }, (_, i) => {
    const car = Math.floor(i / DOORS_PER_CAR)
    const slot = i % DOORS_PER_CAR
    return TX1 + car * CAR_W + ((slot + 1) * CAR_W) / (DOORS_PER_CAR + 1) - DOOR_W / 2
  })

  function principalAxis(pts: Array<{ x: number; y: number }>): number {
    if (pts.length < 2) return 0
    const n = pts.length
    const mx = pts.reduce((s, p) => s + p.x, 0) / n
    const my = pts.reduce((s, p) => s + p.y, 0) / n
    let cxx = 0,
      cyy = 0,
      cxy = 0
    for (const p of pts) {
      cxx += (p.x - mx) ** 2
      cyy += (p.y - my) ** 2
      cxy += (p.x - mx) * (p.y - my)
    }
    return 0.5 * Math.atan2(2 * cxy, cxx - cyy)
  }

  const layout = $derived.by(() => {
    const cosLat = Math.cos((stationLat * Math.PI) / 180)

    const pts = exits
      .filter((e) => e.lat !== 0 || e.lon !== 0)
      .map((e) => ({
        exit: e,
        x: (e.lon - stationLon) * cosLat,
        y: e.lat - stationLat,
      }))

    if (!pts.length) return null

    // PCA: find the platform's longitudinal axis
    const theta = principalAxis(pts)
    const ax = Math.cos(theta)
    const ay = Math.sin(theta)
    const n = pts.length
    const mx = pts.reduce((s, p) => s + p.x, 0) / n
    const my = pts.reduce((s, p) => s + p.y, 0) / n
    const projs = pts.map((p) => (p.x - mx) * ax + (p.y - my) * ay)
    const minP = Math.min(...projs)
    const maxP = Math.max(...projs)
    const span = maxP - minP || 1

    const MARGIN = 0.07
    const toX = (proj: number) =>
      TX1 + (MARGIN + ((proj - minP) / span) * (1 - 2 * MARGIN)) * (TX2 - TX1)

    // Greedy row assignment for exits that have any label (street or corner)
    const withX = pts
      .map((p, i) => ({ i, x: toX(projs[i]) }))
      .filter(({ i }) => !!(pts[i].exit.street || pts[i].exit.corner))
      .sort((a, b) => a.x - b.x)

    const rowLastX: number[] = Array(MAX_ROWS).fill(-Infinity)
    const assignedRow: number[] = new Array(pts.length).fill(0)
    for (const { i, x } of withX) {
      for (let r = 0; r < MAX_ROWS; r++) {
        if (x - rowLastX[r] >= LABEL_GAP) {
          assignedRow[i] = r
          rowLastX[r] = x
          break
        }
      }
    }

    const maxRow = withX.length > 0 ? Math.max(...withX.map(({ i }) => assignedRow[i])) : 0

    return {
      svgH: FIRST_LABEL_Y + maxRow * LABEL_ROW_H + 9,
      marks: pts.map((p, i) => {
        const labelY = FIRST_LABEL_Y + assignedRow[i] * LABEL_ROW_H
        return {
          i,
          ex: toX(projs[i]),
          labelY,
          stemY2: labelY - 5,
          color: exitTypeColor(p.exit.name),
          isElev: p.exit.name.toLowerCase().includes('elevator'),
          label: p.exit.street ? titleCase(p.exit.street) : p.exit.corner,
        }
      }),
    }
  })
</script>

{#if layout}
  <svg
    viewBox="0 0 {W} {layout.svgH}"
    style="width: 100%; height: auto; display: block; border-radius: 10px;"
    aria-label="Station platform diagram"
  >
    <!-- The steel gradient the cars are painted with. Defined here rather than in
         `DIAGRAM` because SVG `fill` takes a paint-server reference, not a CSS gradient. -->
    <defs>
      <linearGradient id="carSteel" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#cfd4dd" />
        <stop offset="1" stop-color="#98a0ad" />
      </linearGradient>
    </defs>

    <rect width={W} height={layout.svgH} fill={DIAGRAM.field} rx="10" />

    <!-- Train body: light against the black panel, so the platform is the lit object. -->
    <path d={TRAIN_PATH} fill="url(#carSteel)" stroke={DIAGRAM.bodyStroke} stroke-width="1" />

    <!-- Doors punched out of the body -->
    <clipPath id="winClip">
      <path d={TRAIN_PATH} />
    </clipPath>
    <g clip-path="url(#winClip)">
      {#each doors as x, i (i)}
        <rect x={x} y={DOOR_Y} width={DOOR_W} height={DOOR_H} rx="1" fill={DIAGRAM.window} />
      {/each}
    </g>

    <!-- Car dividers -->
    {#each carDividers as x, i (i)}
      <line x1={x} y1={TY1 + 1} x2={x} y2={TY2 - 1} stroke={DIAGRAM.carDivider} stroke-width="1" opacity="0.5" />
    {/each}

    <!-- Per-exit: dashed drop line → circle → stem → street label -->
    {#each layout.marks as m (m.i)}
      <g>
        <!-- Dashed drop line from train underside to just above the dot -->
        <line
          x1={m.ex}
          y1={TY2 + 2}
          x2={m.ex}
          y2={CIRCLE_Y - DOT_R - 1}
          stroke={DIAGRAM.dropLine}
          stroke-width="1.5"
          stroke-dasharray="3 2.5"
        />

        <!-- Elevator accent ring -->
        {#if m.isElev}
          <circle
            cx={m.ex}
            cy={CIRCLE_Y}
            r={DOT_R + 3}
            fill="none"
            stroke={m.color}
            stroke-width="1"
            opacity="0.4"
          />
        {/if}

        <!-- Type dot (light = stair, blue = elevator, green = escalator) -->
        <circle
          cx={m.ex}
          cy={CIRCLE_Y}
          r={DOT_R}
          fill={DIAGRAM.dotFill}
          stroke={m.color}
          stroke-width="1.5"
        />

        <!-- Connector stem + label (street name preferred, corner direction as fallback) -->
        {#if m.label}
          <line x1={m.ex} y1={STEM_TOP} x2={m.ex} y2={m.stemY2} stroke={DIAGRAM.stem} stroke-width="1" />
          <text
            x={m.ex}
            y={m.labelY}
            text-anchor="middle"
            dominant-baseline="middle"
            fill={DIAGRAM.label}
            font-size="9"
            font-family="var(--bjl-font-body)"
          >{m.label}</text>
        {/if}
      </g>
    {/each}
  </svg>
{/if}
